<template>
  <div class="slip-overlay" @click.self="close">
    <div class="slip-modal">
      <div class="slip-header">
        <div class="slip-icon"><font-awesome-icon :icon="faBolt" /></div>
        <div class="slip-title-wrap">
          <h3 class="slip-title">Live Slippage</h3>
          <p class="slip-subtitle">
            Fill through a small price move instead of canceling.
          </p>
        </div>
      </div>

      <p class="slip-lead">
        Live odds move fast. Slippage lets a live bet fill up to a tolerance you
        set &mdash; and never worse.
      </p>

      <div class="slip-viz">
        <div class="slip-viz-col">
          <span class="slip-viz-cap">You asked</span>
          <span class="slip-price">&minus;140</span>
        </div>
        <span class="slip-viz-arrow">→</span>
        <div class="slip-viz-col">
          <span class="slip-viz-cap"
            >Worst fill <span class="slip-viz-pct">at 2%</span></span
          >
          <span class="slip-price slip-price--accent">&minus;146</span>
        </div>
      </div>

      <div class="slip-compare">
        <div class="slip-compare-row">
          <span class="slip-tag slip-tag--off">Off</span>
          <span>A tiny move past your price cancels the bet.</span>
        </div>
        <div class="slip-compare-row">
          <span class="slip-tag slip-tag--on">On</span>
          <span>Fills down to your floor &mdash; never worse.</span>
        </div>
      </div>

      <div class="slip-how">
        <span class="slip-how-title">How it works</span>
        <span class="slip-how-item">Drag the dial to your max &mdash; up to 5%.</span>
        <span class="slip-how-item"
          >0% is off, and you can drag, adjust and set a default.</span
        >
      </div>

      <button type="button" class="slip-btn" @click="close">Got it</button>
    </div>
  </div>
</template>

<script setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faBolt } from '@fortawesome/free-solid-svg-icons/faBolt';

const emit = defineEmits(['close']);

function close() {
  emit('close');
}
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/main.scss';

$purple: #744ade;
$purple-soft: rgba(116, 74, 222, 0.1);
$purple-line: rgba(116, 74, 222, 0.22);

.slip-overlay {
  position: fixed;
  inset: 0;
  z-index: 1052; // one above the base modal wrapper (1051)
  background: main.$black-202;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.slip-modal {
  position: relative;
  width: 460px;
  max-width: 100%;
  max-height: calc(100vh - 3rem);
  overflow-y: auto;
  background: main.$white_01;
  color: main.$blue-1100;
  border-radius: 14px;
  box-shadow: 0 1px 5px 0 main.$black-100;
  padding: 1.35rem 1.35rem 1.1rem;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    padding: 1.15rem 1rem 0.9rem;
    border-radius: 12px;
  }
}
.slip-header {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin-bottom: 0.85rem;
}
.slip-icon {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: $purple;
  color: main.$white_01;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}
.slip-title-wrap {
  min-width: 0;
}
.slip-title {
  margin: 0;
  font-weight: main.$font-weight-bold;
  font-size: 1.2rem;
  line-height: 1.15;
}
.slip-subtitle {
  margin: 0.1rem 0 0;
  font-size: 0.82rem;
  line-height: 1.3;
  opacity: 0.6;
}
.slip-lead {
  margin: 0 0 0.85rem;
  font-size: 0.9rem;
  line-height: 1.45;
}
.slip-viz {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 0.6rem 0.8rem;
  margin-bottom: 0.85rem;
  background: $purple-soft;
  border: 1px solid $purple-line;
  border-radius: 10px;
}
.slip-viz-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
}
.slip-viz-cap {
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: main.$font-weight-semibold;
  color: #8a8ea3;
  white-space: nowrap;
}
.slip-viz-pct {
  color: $purple;
}
.slip-price {
  font-size: 1.1rem;
  font-weight: main.$font-weight-bold;
  padding: 0.2rem 0.6rem;
  border-radius: 7px;
  background: main.$white_01;
  border: 1px solid main.$grey-500;
  line-height: 1;
  white-space: nowrap;
  &--accent {
    background: $purple;
    color: main.$white_01;
    border-color: $purple;
  }
}
.slip-viz-arrow {
  font-size: 1rem;
  opacity: 0.45;
}
.slip-compare {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 0.9rem;
}
.slip-compare-row {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  font-size: 0.86rem;
  line-height: 1.3;
}
.slip-tag {
  flex-shrink: 0;
  width: 2.3rem;
  text-align: center;
  padding: 0.14rem 0;
  border-radius: 6px;
  font-size: 0.66rem;
  font-weight: main.$font-weight-bold;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  &--off {
    background: rgba(120, 122, 140, 0.14);
    color: #6a6d80;
  }
  &--on {
    background: $purple-soft;
    color: $purple;
  }
}
.slip-how {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.65rem 0.8rem;
  margin-bottom: 1rem;
  background: rgba(116, 74, 222, 0.05);
  border-radius: 10px;
}
.slip-how-title {
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: main.$font-weight-bold;
  color: $purple;
  margin-bottom: 0.1rem;
}
.slip-how-item {
  position: relative;
  padding-left: 0.9rem;
  font-size: 0.85rem;
  line-height: 1.4;
  &::before {
    content: '•';
    position: absolute;
    left: 0.15rem;
    color: $purple;
  }
}
.slip-btn {
  display: block;
  width: 100%;
  border: 0;
  border-radius: 8px;
  padding: 0.6rem 1rem;
  background: $purple;
  color: main.$white_01;
  font-weight: main.$font-weight-semibold;
  font-size: 0.9rem;
  cursor: pointer;
  transition:
    opacity 0.15s ease,
    transform 0.1s ease;
  &:hover {
    opacity: 0.92;
  }
  &:active {
    transform: translateY(1px);
  }
}
</style>
